/**
 * Life4Earth V2 — Admin Role Rhythms · Add Role tray.
 *
 * DEV-1234 iter 9. Slide-in form for creating a new role definition in
 * the platform catalog. Pillar is pre-filled from whichever pillar tab
 * the admin is on (no picker — admins pick pillar by switching tabs
 * before clicking + Add role).
 *
 * The created role lands in the catalog with:
 *   - owning_entity_guid = null (sysadmin-managed canonical row)
 *   - is_system_locked = true (platform-curated; community edits go via
 *     Pattern B overrides per POLICY_CATALOG_PRESETS §6.2)
 *   - role_rhythm_guid = null (admin attaches a rhythm later via the
 *     auto-create-on-first-task flow from iter 4)
 *
 * Sysadmin-only authoring per CLAUDE.md; server gate at POST layer.
 * English-only (admin UI per CLAUDE.md).
 */

import React, { useEffect, useState } from "react";
import {
  createRoleDefinition,
  type CreateRoleDefinitionRequest,
} from "../../api/roleRhythms";
import styles from "./AddRoleTray.module.css";

interface Props {
  pillarType: string;
  pillarLabel: string;
  open: boolean;
  onClose: () => void;
  /** Called after a successful save so the parent refreshes the catalog. */
  onCreated: (newDefinitionGuid: string) => void;
}

export default function AddRoleTray({
  pillarType,
  pillarLabel,
  open,
  onClose,
  onCreated,
}: Props) {
  const [label, setLabel] = useState("");
  const [roleCategory, setRoleCategory] = useState("custom");
  const [description, setDescription] = useState("");
  const [timeCommitmentHint, setTimeCommitmentHint] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset when the tray opens for a different pillar (closes and reopens).
  useEffect(() => {
    if (open) {
      setLabel("");
      setRoleCategory("custom");
      setDescription("");
      setTimeCommitmentHint("");
      setError(null);
    }
  }, [open, pillarType]);

  const handleSave = async () => {
    const trimmedLabel = label.trim();
    if (!trimmedLabel) {
      setError("Role label is required.");
      return;
    }
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      const req: CreateRoleDefinitionRequest = {
        label: trimmedLabel,
        pillarType,
        roleCategory: roleCategory.trim() || "custom",
        description: description.trim() || null,
        timeCommitmentHint: timeCommitmentHint.trim() || null,
      };
      const created = await createRoleDefinition(req);
      onCreated(created.definitionGuid);
      onClose();
    } catch (err: any) {
      setError(err?.message || "Failed to create role.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        className={styles.backdrop}
        onClick={() => { if (!saving) onClose(); }}
        aria-hidden="true"
      />
      <aside className={styles.tray} role="dialog" aria-labelledby="add-role-title">
        <header className={styles.header}>
          <div>
            <div className={styles.eyebrow}>{pillarLabel}</div>
            <h2 id="add-role-title" className={styles.title}>Add role</h2>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            disabled={saving}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        {error && <div className={styles.errorBanner}>{error}</div>}

        <div className={styles.body}>
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Label</span>
            <input
              type="text"
              className={styles.textInput}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Membership Chair"
              maxLength={200}
              autoFocus
            />
            <span className={styles.fieldHint}>
              The display name that appears in role pickers and assignments.
            </span>
          </label>

          {/* Hotfix 2026-06-05 — role_category is a closed enum at the
              DB layer (chk_rd_category, see migration 200). Skip hit the
              CHECK constraint when this was a free-text input. The 13
              values below are the canonical list — adding new ones
              requires a migration. Long-term this should move to an
              admin-managed catalog like entity_types (DEV-1235 pattern). */}
          <label className={styles.field}>
            <span className={styles.fieldLabel}>Category</span>
            <select
              className={styles.textInput}
              value={roleCategory}
              onChange={(e) => setRoleCategory(e.target.value)}
            >
              <option value="custom">Custom</option>
              <option value="leadership">Leadership</option>
              <option value="coordination">Coordination</option>
              <option value="advocacy">Advocacy</option>
              <option value="communications">Communications</option>
              <option value="content">Content</option>
              <option value="creative">Creative</option>
              <option value="education">Education</option>
              <option value="enablement">Enablement</option>
              <option value="technical">Technical</option>
              <option value="research">Research</option>
              <option value="data_collection">Data collection</option>
              <option value="labor">Labor</option>
            </select>
            <span className={styles.fieldHint}>
              Used to sub-group the role list when a pillar has more than
              10 roles. Pick the closest match — adding a new category
              requires a database migration (chk_rd_category constraint).
            </span>
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Description</span>
            <textarea
              className={styles.textarea}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What this role does, who it's for, what success looks like."
              rows={3}
            />
          </label>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Time commitment (optional)</span>
            <input
              type="text"
              className={styles.textInput}
              value={timeCommitmentHint}
              onChange={(e) => setTimeCommitmentHint(e.target.value)}
              placeholder="e.g., 2-3 hours per month"
              maxLength={100}
            />
          </label>
        </div>

        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={handleSave}
            disabled={saving || !label.trim()}
          >
            {saving ? "Creating…" : "Create role"}
          </button>
        </footer>
      </aside>
    </>
  );
}
